<template>
  <div :class="`${type}-address-form`">
    <div class="form-field-container">
      <div class="field__group">
        <ValidationProvider class="checkout__field firstname" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Name')">
          <label>{{ $t('Name') }} *</label>
          <input type="text" :placeholder="$t('Name')" :name="`${type}-address[firstname]`" v-model="inputAddress.firstname" :class="classes"/>
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
        <ValidationProvider class="checkout__field lastname" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Last name')">
          <label>{{ $t('Last name') }} *</label>
          <input type="text" :placeholder="$t('Last name')" :name="`${type}-address[lastname]`" v-model="inputAddress.lastname" :class="classes"/>
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>

      <div class="field__group business-toggle" v-if="!isLoggedIn">
        <input
          :id="`${type}-is-business`"
          type="checkbox"
          v-model="isBusiness"
        />
        <label :for="`${type}-is-business`">{{ $t('Business order') }}</label>
      </div>

      <template v-if="isBusiness">
        <div class="field__group">
          <ValidationProvider class="checkout__field company" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Company')">
            <label>{{ $t('Company') }} *</label>
            <input type="text" :placeholder="$t('Company')" :name="`${type}-address[company]`" v-model="inputAddress.company" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
        </div>
        <div class="field__group">
          <ValidationProvider class="checkout__field vat_id" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('VAT Number')">
            <label>{{ $t('VAT Number') }} *</label>
            <input type="text" :placeholder="$t('VAT Number')" :name="`${type}-address[vat_id]`" v-model="inputAddress.vat_id" :class="classes" @change="validateVatNumber"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
            <span class="vat-validation-message _valid" v-else-if="vatValidation && vatValidation.valid">{{ $t(vatValidation.message) }}<template v-if="vatValidation.trader_name"> ({{ vatValidation.trader_name }})</template></span>
            <span class="vat-validation-message _invalid" v-else-if="vatValidation && !vatValidation.valid">{{ $t(vatValidation.message) }}</span>
          </ValidationProvider>
        </div>
      </template>

      <div class="field__group" v-if="shippingCountries.length">
        <ValidationProvider class="checkout__field country" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Country')">
          <label>{{ $t('Country') }}</label>
          <select :name="`${type}-address[country]`" v-model="inputAddress.country_id" :class="classes">
            <option value="" selected disabled>{{ $t('Choose your country') }}</option>
            <option :value="country.two_letter_abbreviation" v-for="country in shippingCountries" :key="country.two_letter_abbreviation">{{ country.full_name_locale }}</option>
          </select>
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>

      <checkout-address-autocomplete
        v-if="addressAutocompleteEnabled && (inputAddress.country_id === 'NL' || inputAddress.country_id === '')"
        :type="type"
      ></checkout-address-autocomplete>
      <template v-else>
        <div class="field__group">
          <ValidationProvider class="checkout__field street_0" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Street')">
            <label>{{ $t('Street') }} *</label>
            <input type="text" :placeholder="$t('Street')" v-model="inputAddress['street'][0]" :name="`${type}-address[street][0]`" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
          <ValidationProvider class="checkout__field street_1" v-slot="{ errors, classes }" rules="required|integer" :skip-if-empty="false" tag="div" :name="$t('House number')">
            <label>{{ $t('House number') }} *</label>
            <input :placeholder="$t('House no.')" type="number" :name="`${type}-address[street][1]`" v-model.trim="inputAddress['street'][1]" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
          <ValidationProvider class="checkout__field street_2" v-slot="{ errors, classes }" :skip-if-empty="false" tag="div" :name="$t('Addition')">
            <label>{{ $t('Addition') }}</label>
            <input type="text" :placeholder="$t('Add.')" :name="`${type}-address[street][2]`" maxlength="10" v-model.trim="inputAddress['street'][2]" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
        </div>
        <div class="field__group">
          <ValidationProvider class="checkout__field postcode" v-slot="{ errors, classes }" :rules="{ required: true, regex: (inputAddress.country_id === 'NL' || inputAddress.country_id === '') ? /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i : '' }" :skip-if-empty="false" tag="div" :name="$t('Postal code')">
            <label>{{ $t('Postal code') }} *</label>
            <input type="text" :placeholder="$t('Postal code')" maxlength="6" :name="`${type}-address[postcode]`" v-model.trim="inputAddress.postcode" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
          <ValidationProvider class="checkout__field city" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('City')">
            <label>{{ $t('City') }} *</label>
            <input type="text" :placeholder="$t('City')" v-model="inputAddress.city" :name="`${type}-address[city]`" :class="classes"/>
            <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
          </ValidationProvider>
        </div>
      </template>

      <div class="field__group">
        <ValidationProvider class="checkout__field telephone _last" v-slot="{ errors, classes }" rules="required|integer|min:10|max:14" :skip-if-empty="false" tag="div" :name="$t('Telephone')">
          <label>{{ $t('Telephone') }} *</label>
          <input type="tel" minlength="10" maxlength="14" :placeholder="$t('Telephone')" :name="`${type}-address[telephone]`" v-model="inputAddress.telephone" :class="classes"/>
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {Component, Vue, Prop, Watch} from 'vue-property-decorator';
  import {ValidationProvider, extend} from 'vee-validate';
  import {required, integer, min, max, regex} from 'vee-validate/dist/rules';

  extend('required', required);
  extend('integer', integer);
  extend('min', min);
  extend('max', max);
  extend('regex', regex);

  @Component({
    name: 'checkout-address-form',
    components: {
      ValidationProvider,
      'checkout-address-autocomplete': require('./AddressAutocomplete.vue').default,
    }
  })

  export default class extends Vue {
    @Prop({required: true}) private type: string;
    @Prop({default: false}) private postcodeValidationEnabled: boolean;
    @Prop({default: false}) private isLoggedIn: boolean;
    @Prop({default: false}) private isBusinessAccount: boolean;

    private addressAutocompleteEnabled: boolean = true;
    private isBusiness: boolean = false;
    private vatValidation: any = null;
    private vatValidationTimer: any = null;

    private inputAddress: any = {
      prefix: '',
      firstname: '',
      middlename: '',
      lastname: '',
      postcode: '',
      street: ['', '', ''],
      city: '',
      telephone: '',
      company: '',
      vat_id: '',
      coc_number: '',
      country_id: 'NL'
    };

    @Watch('inputAddress', {deep: true})
    handler(nv): void {
      if (this.type === 'shipping') {
        this.$store.dispatch('CheckoutAddress/setShippingAddress', {
          address: nv
        });
        return;
      }

      this.$store.dispatch('CheckoutAddress/setBillingAddress', {
        address: nv
      });
    }

    @Watch('isBusiness')
    onBusinessToggle(value: boolean): void {
      this.$store.commit('CheckoutGlobal/SET_IS_BUSINESS_ACCOUNT', value);

      if (!value) {
        this.inputAddress.company = '';
        this.inputAddress.vat_id = '';
        this.inputAddress.coc_number = '';
        this.vatValidation = null;
      }
    }

    @Watch('inputAddress.vat_id')
    onVatIdChange(value: string): void {
      this.vatValidation = null;

      if (this.vatValidationTimer) {
        clearTimeout(this.vatValidationTimer);
      }

      const vatNumber = (value || '').trim();
      if (vatNumber.length < 4) {
        return;
      }

      this.vatValidationTimer = setTimeout(() => this.validateVatNumber(), 800);
    }

    private async validateVatNumber(): Promise<void> {
      const vatNumber = (this.inputAddress.vat_id || '').replace(/[\W_]/g, '').toUpperCase().trim();

      if (vatNumber.length < 4) {
        return;
      }

      // Ensure the number carries the country code, like the Luma field did.
      const withCountry = /^[A-Z]{2}/.test(vatNumber)
        ? vatNumber
        : `${this.inputAddress.country_id || 'NL'}${vatNumber}`;

      try {
        const {data} = await this.$solarClient.post('/api/checkout/vat/validate', {vat_number: withCountry});
        this.vatValidation = data;
      } catch (e) {
        this.vatValidation = {
          valid: false,
          message: 'The VAT number could not be validated at this time.',
        };
      }
    }

    private get shippingAddress(): object {
      return this.$store.state.CheckoutAddress.shippingAddress;
    }

    private get billingAddress(): object {
      return this.$store.state.CheckoutAddress.billingAddress;
    }

    private get shippingCountries(): Array<object> {
      const countries = this.$store.getters['CheckoutShipping/shippingCountries'] || [];
      return countries.length ? [...countries].sort((a, b) => (a.full_name_locale > b.full_name_locale) ? 1 : -1) : [];
    }

    private looksLikeBusiness(address: any): boolean {
      const vatId = (address?.vat_id || '').toString().trim();
      const company = (address?.company || '').toString().trim();
      const coc = (address?.coc_number || '').toString().trim();
      return !!(vatId || company || coc);
    }

    private async mounted(): Promise<void> {
      try {
        const {data} = await this.$solarClient.get('/api/postcode/enabled');
        this.addressAutocompleteEnabled = !!data?.enabled;
      } catch (e) {
        this.addressAutocompleteEnabled = false;
      }

      if (this.type === 'shipping' && Object.keys(this.shippingAddress).length > 0) {
        if (this.shippingAddress['newAddress']) {
          this.inputAddress = {
            newAddress: true,
            prefix: '',
            firstname: '',
            middlename: '',
            lastname: '',
            postcode: '',
            street: ['', '', ''],
            city: '',
            telephone: '',
            company: '',
            vat_id: '',
            coc_number: '',
            country_id: 'NL',
          };
          this.$store.dispatch('CheckoutAddress/setShippingAddress', {
            address: this.inputAddress,
          });
        } else {
          this.inputAddress = Object.assign(this.inputAddress, this.shippingAddress);
        }
      }

      if (this.type === 'billing' && Object.keys(this.billingAddress).length > 0) {
        this.inputAddress = Object.assign(this.inputAddress, this.billingAddress);
      }

      if (!this.inputAddress.street || !Array.isArray(this.inputAddress.street)) {
        this.inputAddress.street = ['', '', ''];
      }

      const existingCoc = this.inputAddress.coc_number
        || this.inputAddress?.extension_attributes?.coc_number
        || '';
      this.inputAddress.coc_number = existingCoc;

      if (this.isLoggedIn) {
        this.isBusiness = this.isBusinessAccount || this.looksLikeBusiness(this.inputAddress);
      } else if (this.looksLikeBusiness(this.inputAddress) || this.$store.state.CheckoutGlobal?.isBusinessAccount) {
        this.isBusiness = true;
      }
    }
  }
</script>
